import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { FloatingTableOfContents } from "../../components/FloatingTableOfContents";
import { TableOfContents } from "../../components/TableOfContents";
import { getMarkdownBlogPostBySlug, getRelatedBlogPosts } from "../../lib/blog";
import { SITE_NAME, applyPageSeo } from "../../lib/seo";

export function MarkdownBlogPostRoute() {
  const { slug = "" } = useParams();
  const post = getMarkdownBlogPostBySlug(slug);

  useEffect(() => {
    if (!post) return;

    const cleanup = applyPageSeo({
      title: post.title,
      description: post.description,
      path: post.href,
      ogImage: post.ogImage,
      keywords: post.tags,
      canonicalUrl: post.canonical,
      appendSiteName: false,
    });

    const articleJsonLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.description,
      url: `${window.location.origin}${post.href}`,
      datePublished: post.date,
      dateModified: post.date,
      author: {
        "@type": "Organization",
        name: SITE_NAME,
      },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
      },
      image: [post.ogImage],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "article-jsonld";
    script.textContent = JSON.stringify(articleJsonLd);
    document.head.appendChild(script);

    return () => {
      cleanup();
      document.getElementById("article-jsonld")?.remove();
    };
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = getRelatedBlogPosts(post.href);

  return (
    <div className="bg-white text-slate-950">
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-4 flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-400 transition hover:text-slate-600">
              Home
            </Link>
            <svg aria-hidden="true" className="h-3.5 w-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <Link to="/blog" className="text-slate-400 transition hover:text-slate-600">
              Blog
            </Link>
            <svg aria-hidden="true" className="h-3.5 w-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-slate-700">{post.title}</span>
          </nav>

          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="rounded bg-sky-50 px-3 py-1 font-semibold text-sky-700">{post.category}</span>
              <span className="text-slate-500">{post.readTime}</span>
              {post.date ? <span className="text-slate-500">Published {post.date}</span> : null}
            </div>
            <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">{post.title}</h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600 sm:text-xl">{post.description}</p>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-[minmax(0,1fr)_280px] lg:px-8">
          <article className="min-w-0 max-w-3xl">
            <div className="mb-8 lg:hidden">
              <TableOfContents items={post.tocItems} />
            </div>
            <div dangerouslySetInnerHTML={{ __html: post.html }} />
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <TableOfContents items={post.tocItems} />
              <div className="rounded-md border border-slate-200 bg-slate-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Try the product</p>
                <h2 className="mt-3 text-lg font-semibold text-slate-950">Build React Email templates faster</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">Generate, edit, and export production-ready email templates straight from the browser.</p>
                <div className="mt-4 flex flex-col gap-3">
                  <Link to="/builder" className="inline-flex items-center justify-center rounded bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800">
                    Start building
                  </Link>
                  <Link to="/discover/templates" className="inline-flex items-center justify-center rounded border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-950">
                    Browse templates
                  </Link>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <FloatingTableOfContents items={post.tocItems} />

      <section className="border-t border-slate-200 bg-slate-50 py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-700">Continue reading</p>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">Related articles</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {relatedPosts.map((article) => (
              <Link
                key={article.href}
                to={article.href}
                className="group rounded-md border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-200/60"
              >
                <span className="rounded-md bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-sky-700">
                  {article.category}
                </span>
                <h3 className="mt-3 text-lg font-semibold text-slate-950 transition group-hover:text-sky-700">{article.title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{article.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
